/**
 * @fileoverview Pure logic behind the Lessons page — the period filter, the
 * metrics rail, the chart buckets and the export field table.
 *
 * Deliberately free of React and of `lib/api`: every number the page shows is
 * derived here from one array of lessons, so the list, the metrics and the
 * exported file can never disagree about what "this quarter" means. The page
 * fetches the full history once and everything below is computed client-side.
 */

const DAY = 24 * 60 * 60 * 1000;

/* ------------------------------------------------------------------ */
/*  Periods                                                            */
/* ------------------------------------------------------------------ */

/**
 * The period filter.
 *
 * The DEFAULT is a rolling 30 days, not "this month", and the reason is the
 * calendar: on the 1st, "this month" is one or two lessons, so the page most
 * instructors open least often is also the one that greets them with an empty
 * chart and a weekly average of nothing. A rolling window always has a month
 * of teaching in it. "This month" stays one click away — it is the window a
 * bill is drawn on — and `all` is last because it is the fallback.
 */
export const PERIODS = [
  { id: "last_30_days", label: "Last 30 days" },
  { id: "this_month", label: "This month" },
  { id: "last_month", label: "Last month" },
  { id: "this_quarter", label: "This quarter" },
  { id: "this_year", label: "This year" },
  { id: "last_12_months", label: "Last 12 months" },
  { id: "all", label: "All time" },
];

export const DEFAULT_PERIOD = "last_30_days";

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/**
 * Resolve a period id to a `[start, end)` window in LOCAL time.
 *
 * Local, not UTC, because an instructor reasons about "this month" in the
 * month they were standing in when they taught — a UTC boundary would move a
 * 7pm lesson on the 31st into the next month for anyone west of Greenwich.
 *
 * `start: null` means unbounded (all time). `end` is always exclusive.
 */
export function periodRange(periodId, now = new Date()) {
  const y = now.getFullYear();
  const m = now.getMonth();
  const endOfToday = new Date(startOfDay(now).getTime() + DAY);

  switch (periodId) {
    case "last_30_days":
      return { start: new Date(startOfDay(now).getTime() - 29 * DAY), end: endOfToday };
    case "this_month":
      return { start: new Date(y, m, 1), end: endOfToday };
    case "last_month":
      // The only period with a hard end in the past: last month is finished,
      // so it must not creep forward as today moves.
      return { start: new Date(y, m - 1, 1), end: new Date(y, m, 1) };
    case "this_quarter":
      return { start: new Date(y, Math.floor(m / 3) * 3, 1), end: endOfToday };
    case "this_year":
      return { start: new Date(y, 0, 1), end: endOfToday };
    case "last_12_months":
      return { start: new Date(y - 1, m, 1), end: endOfToday };
    case "all":
    default:
      return { start: null, end: endOfToday };
  }
}

export function periodLabel(periodId) {
  return PERIODS.find((p) => p.id === periodId)?.label || "All time";
}

/* ------------------------------------------------------------------ */
/*  Lesson shape helpers                                               */
/* ------------------------------------------------------------------ */

export const isClassLesson = (lesson) => lesson?.kind === "class";

/** A lesson's date as a Date, or null when the row carries no usable stamp. */
export function lessonDate(lesson) {
  const raw = lesson?.created_at;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Local YYYY-MM-DD of a Date — the day the person looking at the list is in. */
const localDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * A lesson written ahead of its session.
 *
 * Its scheduled date is after today, so the meeting has not happened yet
 * whatever has been typed against it. Such a lesson is listed in full and
 * labelled Planned; it is never counted as taught. The rule is the same one
 * the backend applies to every "sessions taught" number
 * (`services/teaching_counts.is_planned`): a lesson with no scheduled date
 * belongs to the day it was written, which is never in the future.
 */
export function isPlanned(lesson, now = new Date()) {
  const day = String(lesson?.scheduled_date || "").slice(0, 10);
  return Boolean(day) && day > localDay(now);
}

/**
 * Whether the summary reached the family.
 *
 * `email_sent` is the only fact that says so, and its ABSENCE is meaningful:
 * a lesson recorded before the flag existed, or one that simply was never
 * sent, both read as not-shared. This is deliberately the same equality the
 * backend's PORTAL_VISIBLE_LESSON filter uses — a lesson the student can see
 * and a lesson marked shared here must be the same set.
 */
export const isShared = (lesson) => lesson?.email_sent === true;

/* ------------------------------------------------------------------ */
/*  Order                                                              */
/* ------------------------------------------------------------------ */

/**
 * When a row happened, in epoch milliseconds — null when it carries no date.
 *
 * The day is the one the lesson belongs to: its session's (`scheduled_date`,
 * or a session row's `date`), else the day it was recorded — the backend's
 * own rule (`services/teaching_counts.lesson_day`), so a lesson planned for
 * next week sits with next week's meetings, not on the day it was typed. The
 * time is the meeting's (`time_local`, instructor-local), else the recording's
 * when it was made that day, else midday.
 */
export function lessonTime(row) {
  const created = lessonDate(row);
  const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(row?.scheduled_date || row?.date || ""));
  if (!day) return created ? created.getTime() : null;
  const [y, m, d] = [+day[1], day[2] - 1, +day[3]];
  const clock = /^(\d{1,2}):(\d{2})/.exec(String(row?.time_local || ""));
  if (clock) return new Date(y, m, d, +clock[1], +clock[2]).getTime();
  const sameDay = created && created.getFullYear() === y && created.getMonth() === m && created.getDate() === d;
  return sameDay ? created.getTime() : new Date(y, m, d, 12).getTime();
}

/**
 * When a lesson was recorded (`created_at`), in epoch milliseconds — the date
 * a list of lessons alone prints beside each one, and so the one it orders by:
 * a recording uploaded the Monday after Friday's lesson says Monday, and sits
 * there.
 */
export function recordedAt(lesson) {
  const d = lessonDate(lesson);
  return d ? d.getTime() : null;
}

/**
 * Oldest first: every list of lessons and sessions reads in the order they
 * were taught, so a term reads top to bottom. The server still answers newest
 * first — that is what makes `limit` mean "the latest N" — and the lists turn
 * it round here. A list that shows only the latest few takes them from the
 * end: `sortChronologically(rows, recordedAt).slice(-4)`.
 *
 * `timeOf` places a row: `lessonTime` for sessions and lessons together (a
 * class's term), `recordedAt` for a list of lessons that prints the recorded
 * date. Undated rows go last; rows it cannot tell apart keep their order.
 */
export function sortChronologically(rows, timeOf = lessonTime) {
  return (rows || [])
    .map((row, index) => ({ row, index, at: timeOf(row) }))
    .sort((a, b) => (a.at === null) - (b.at === null) || (a.at ?? 0) - (b.at ?? 0) || a.index - b.index)
    .map(({ row }) => row);
}

/** How many rows a long list shows at a time: 25, or 50 or 100 on request. */
export const PAGE_SIZES = [25, 50, 100];
export const DEFAULT_PAGE_SIZE = PAGE_SIZES[0];

/**
 * The first `count` rows of an oldest-first list, and how many later rows that
 * leaves out. A long list opens at the start of its range — its first row is
 * the earliest lesson the range holds — and reveals the later ones below, so
 * it reads top to bottom in the order they were taught.
 */
export function firstRows(rows, count) {
  const list = rows || [];
  const shown = list.slice(0, Math.max(0, count));
  return { shown, more: list.length - shown.length };
}

/* ------------------------------------------------------------------ */
/*  Filtering                                                          */
/* ------------------------------------------------------------------ */

export const KINDS = [
  { id: "all", label: "All" },
  { id: "student", label: "Individual" },
  { id: "class", label: "Classes" },
];

/**
 * What became of a row (F4). Four answers, one word each:
 * `written` — a lesson exists and its day has come; `planned` — a lesson
 * typed ahead of a future session; `missing` — a past session that happened
 * and was never written up (a row the caller merged from the schedule, not a
 * lesson); `cancelled` — a session that did not happen. The org's and the
 * instructor's lists filter on the same word.
 */
export const STATUSES = [
  { id: "all", label: "All" },
  { id: "written", label: "Written" },
  { id: "missing", label: "Missing notes" },
  { id: "cleared", label: "Notes cleared" },
  { id: "planned", label: "Planned" },
  { id: "scheduled", label: "Upcoming" },
  { id: "cancelled", label: "Cancelled" },
];

/** Whether `instructor` taught (or owes) this row: its writer, or, for a
    session nobody has written up yet, any of the class's active instructors. */
function taughtBy(row, instructor) {
  if ((row.user_id || row.instructor_id) === instructor) return true;
  return Array.isArray(row.instructor_ids) && row.instructor_ids.includes(instructor);
}

export function lessonStatus(row, now = new Date()) {
  // A session a substitute covers for someone else (docs/substitute-requests.md):
  // listed, but neither written nor missing for the reader — it is not theirs.
  if (row?.covered && !row.covered.mine) return "covered";
  if (row?.status === "cancelled") return "cancelled";
  // A past session whose missing-notes notice was cleared (lib/notesClearance):
  // it happened, nobody owes notes for it, and it is not a written lesson.
  if (row?.notes_cleared) return "cleared";
  if (row?.missing) return "missing";
  // A future meeting nobody has written against yet: not planned (nothing
  // was typed), not missing (it has not happened) — simply still to come.
  if (row?.scheduled) return "scheduled";
  if (row?.planned || isPlanned(row, now)) return "planned";
  return "written";
}

/**
 * A past session that happened and has no notes, as a row the filter and the
 * lists can hold next to lessons: dated by the session, titled by the class,
 * and marked `missing` so nothing mistakes it for a written lesson.
 */
export function missingRow(session, { classId, className, instructorId, instructorIds, instructorName, status } = {}) {
  const date = session.date || session.scheduled_date;
  const cancelled = status === "cancelled" || session.status === "cancelled";
  // Cleared, the session owes nothing; asked about, it still does and says so.
  const cleared = !cancelled && session.notes_clearance?.status === "cleared";
  return {
    lesson_id: `${cancelled ? "cancelled" : "missing"}:${classId || session.class_id || ""}:${session.occurrence_id || date}`,
    kind: "class",
    missing: !cancelled && !cleared,
    notes_cleared: cleared || undefined,
    notes_clearance: session.notes_clearance || null,
    notes_clear_mode: session.notes_clear_mode || null,
    date,
    status: cancelled ? "cancelled" : undefined,
    instructor_ids: instructorIds || session.instructor_ids || undefined,
    class_id: classId || session.class_id || null,
    title: className || session.display_name || "Class session",
    scheduled_date: date,
    created_at: date ? `${date}T12:00:00` : undefined,
    time_local: session.time_local,
    occurrence_id: session.occurrence_id,
    user_id: instructorId || session.instructor_id || null,
    instructor_name: instructorName || session.instructor_name || null,
    substitute: session.substitute || null,
    // The organization's name for the session and its archive of it
    // (org_session_marks), on an org class page's meetings; absent elsewhere.
    session_key: session.session_key || undefined,
    archived: session.archived || undefined,
  };
}

/**
 * Apply the whole filter bar in one pass.
 *
 * `nameFor` maps a lesson to its student-or-class name so the search box can
 * match on it — the name lives in a separate roster fetch, not on the lesson.
 */
export function filterLessons(
  lessons,
  { period, kind, search, sharedOnly, unsentOnly, instructor, classId, program, status },
  nameFor,
  now = new Date(),
) {
  const { start, end } = periodRange(period, now);
  const q = (search || "").trim().toLowerCase();

  return (lessons || []).filter((lesson) => {
    const d = lessonDate(lesson);
    // An undated row is kept only under "All time": it cannot be shown to
    // belong to a period, and silently filing it under the current one would
    // put a lesson of unknown date into a number someone might invoice from.
    if (start || period !== "all") {
      if (!d) return period === "all";
      if (start && d < start) return false;
      if (d >= end) return false;
    }

    if (kind === "student" && isClassLesson(lesson)) return false;
    if (kind === "class" && !isClassLesson(lesson)) return false;

    // The shared filter model (F4): who taught it, which class, which
    // program, and what became of it. Each is ignored when unset.
    if (instructor && instructor !== "all" && !taughtBy(lesson, instructor)) return false;
    if (classId && classId !== "all" && lesson.class_id !== classId) return false;
    if (program && program !== "all" && lesson.program_id !== program) return false;
    if (status && status !== "all" && lessonStatus(lesson, now) !== status) return false;

    if (sharedOnly && !isShared(lesson)) return false;
    if (unsentOnly && isShared(lesson)) return false;

    if (q) {
      const haystack = `${lesson.title || ""} ${nameFor?.(lesson) || ""} ${lesson.instructor_name || ""}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}

/**
 * The classes that actually appear in a history, for a class selector that
 * never offers an empty result (I10). `nameFor` names a class row the way
 * the list does, so the control and the rows agree.
 */
export function classesInHistory(lessons, nameFor) {
  const seen = new Map();
  for (const lesson of lessons || []) {
    if (!isClassLesson(lesson) || !lesson.class_id || seen.has(lesson.class_id)) continue;
    seen.set(lesson.class_id, nameFor?.(lesson) || lesson.title || "Class");
  }
  return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Walk the whole lesson history. Metrics, the period filter and the export
 * are all whole-history questions; answering them from page one would put a
 * number on screen that looks authoritative and is not.
 */
export async function fetchLessonHistory(api, { signal, pageSize = 200, maxPages = 50 } = {}) {
  const all = [];
  for (let page = 0; page < maxPages; page += 1) {
    const res = await api.get(`/lessons?limit=${pageSize}&skip=${all.length}`, { signal });
    const batch = res.data || [];
    all.push(...batch);
    if (batch.length < pageSize) break;
  }
  return all;
}

/* ------------------------------------------------------------------ */
/*  Metrics                                                            */
/* ------------------------------------------------------------------ */

/** Whole weeks the period has actually been running, floored at 1. */
function weeksElapsed(lessons, period, now) {
  const { start, end } = periodRange(period, now);
  const upTo = Math.min(end.getTime(), now.getTime() + DAY);

  let from = start ? start.getTime() : null;
  if (from === null) {
    // All time starts at the first lesson, not at the epoch — dividing by
    // "weeks since 1970" would report a weekly average of zero for everyone.
    const stamps = lessons.map(lessonDate).filter(Boolean).map((d) => d.getTime());
    from = stamps.length ? Math.min(...stamps) : upTo;
  }
  return Math.max(1, (upTo - from) / (7 * DAY));
}

/**
 * The metrics rail's numbers, all derived from the ALREADY-FILTERED list so
 * they describe exactly the rows on screen.
 */
export function computeMetrics(filtered, { period, allLessons = filtered, now = new Date() } = {}) {
  const count = filtered.length;
  const seconds = filtered.reduce((sum, l) => sum + (Number(l.duration_seconds) || 0), 0);
  const unsent = filtered.filter((l) => !isShared(l)).length;
  const classCount = filtered.filter(isClassLesson).length;

  const students = new Set();
  for (const l of filtered) {
    if (isClassLesson(l)) students.add(`class:${l.class_id}`);
    else if (l.student_id) students.add(l.student_id);
  }

  return {
    count,
    classCount,
    studentCount: students.size,
    seconds,
    hours: seconds / 3600,
    avgSeconds: count ? seconds / count : 0,
    unsent,
    sharedPct: count ? Math.round(((count - unsent) / count) * 100) : 0,
    perWeek: count / weeksElapsed(allLessons, period, now),
  };
}

/* ------------------------------------------------------------------ */
/*  Chart buckets                                                      */
/* ------------------------------------------------------------------ */

/**
 * Bucket width is chosen from the span so the chart lands at a readable
 * number of bars — a year in weeks is 52 unreadable slivers, a month in
 * months is one bar, which is a stat tile pretending to be a chart.
 */
export function bucketGranularity(spanDays) {
  if (spanDays <= 45) return "week";
  if (spanDays <= 400) return "month";
  return "quarter";
}

const mondayOf = (d) => {
  const out = startOfDay(d);
  out.setDate(out.getDate() - ((out.getDay() + 6) % 7));
  return out;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Lessons over time, as an unbroken series of buckets across the period.
 *
 * Empty buckets are EMITTED rather than skipped: a week with no lessons is a
 * fact about the studio, and dropping it would silently compress a quiet
 * August into the shape of a busy one.
 */
export function bucketOverTime(filtered, period, now = new Date()) {
  const { end } = periodRange(period, now);
  const stamps = filtered.map(lessonDate).filter(Boolean);
  const rangeStart = periodRange(period, now).start;
  const first = rangeStart || (stamps.length ? new Date(Math.min(...stamps.map((d) => d.getTime()))) : startOfDay(now));
  const last = new Date(Math.min(end.getTime(), now.getTime() + DAY));

  const spanDays = Math.max(1, (last - first) / DAY);
  const grain = bucketGranularity(spanDays);

  const keyOf = (d) => {
    if (grain === "week") return mondayOf(d).toISOString().slice(0, 10);
    if (grain === "month") return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    return `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`;
  };
  const labelOf = (d) => {
    if (grain === "week") return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
    if (grain === "month") return MONTHS[d.getMonth()];
    return `Q${Math.floor(d.getMonth() / 3) + 1}`;
  };

  const buckets = [];
  const index = new Map();
  const cursor = grain === "week" ? mondayOf(first) : new Date(first.getFullYear(), grain === "month" ? first.getMonth() : Math.floor(first.getMonth() / 3) * 3, 1);
  // Hard cap: a corrupt future date must not spin this loop forever.
  for (let guard = 0; cursor < last && guard < 200; guard += 1) {
    const key = keyOf(cursor);
    const bucket = { key, label: labelOf(cursor), year: cursor.getFullYear(), count: 0, seconds: 0 };
    index.set(key, bucket);
    buckets.push(bucket);
    if (grain === "week") cursor.setDate(cursor.getDate() + 7);
    else cursor.setMonth(cursor.getMonth() + (grain === "month" ? 1 : 3));
  }

  for (const lesson of filtered) {
    const d = lessonDate(lesson);
    if (!d) continue;
    const bucket = index.get(keyOf(d));
    if (!bucket) continue;
    bucket.count += 1;
    bucket.seconds += Number(lesson.duration_seconds) || 0;
  }
  return { grain, buckets };
}

/**
 * Lessons per student (classes counted as their own row), busiest first.
 *
 * Past `limit` the tail folds into one "Others" row rather than growing the
 * chart — the question this answers is "who am I teaching most", and the
 * eleventh name has never been the answer to it.
 */
export function bucketByStudent(filtered, nameFor, limit = 6) {
  const rows = new Map();
  for (const lesson of filtered) {
    const key = isClassLesson(lesson) ? `class:${lesson.class_id}` : `student:${lesson.student_id}`;
    const existing = rows.get(key) || {
      key,
      name: nameFor?.(lesson) || (isClassLesson(lesson) ? "Class lesson" : "Unknown student"),
      isClass: isClassLesson(lesson),
      count: 0,
      seconds: 0,
    };
    existing.count += 1;
    existing.seconds += Number(lesson.duration_seconds) || 0;
    rows.set(key, existing);
  }

  const sorted = [...rows.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  if (sorted.length <= limit) return sorted;

  const head = sorted.slice(0, limit);
  const tail = sorted.slice(limit);
  head.push({
    key: "others",
    name: `${tail.length} others`,
    isClass: false,
    isOther: true,
    count: tail.reduce((s, r) => s + r.count, 0),
    seconds: tail.reduce((s, r) => s + r.seconds, 0),
  });
  return head;
}

/* ------------------------------------------------------------------ */
/*  Export                                                             */
/* ------------------------------------------------------------------ */

/**
 * The exportable columns.
 *
 * `preset: true` is what the field picker ticks on open. The preset is the
 * spreadsheet an instructor actually builds by hand — date, who, what, how
 * long, was it sent — and the rest are there for the one person who needs
 * them, not to pad the default.
 */
export const EXPORT_FIELDS = [
  { id: "date", label: "Date", preset: true, value: (l) => (lessonDate(l) ? lessonDate(l).toISOString().slice(0, 10) : "") },
  { id: "time", label: "Time", preset: false, value: (l) => {
    const d = lessonDate(l);
    return d ? `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` : "";
  } },
  { id: "title", label: "Lesson title", preset: true, value: (l) => l.title || "" },
  { id: "name", label: "Student / class", preset: true, value: (l, ctx) => ctx.nameFor?.(l) || "" },
  { id: "type", label: "Type", preset: true, value: (l) => (isClassLesson(l) ? "Class" : "Individual") },
  { id: "duration_minutes", label: "Duration (minutes)", preset: true, value: (l) => Math.round((Number(l.duration_seconds) || 0) / 60) },
  { id: "shared", label: "Notes shared", preset: true, value: (l) => (isShared(l) ? "Yes" : "No") },
  { id: "instrument", label: "Instrument", preset: false, value: (l) => l.instrument || "" },
  { id: "source", label: "Source", preset: false, value: (l) => (l.manual ? "Typed" : "Recorded") },
  { id: "program", label: "Program", preset: false, value: (l) => l.program_title || "" },
  { id: "organization", label: "Organization", preset: false, value: (l) => l.org_name || "" },
  { id: "summary", label: "Summary", preset: false, value: (l) => l.summary || "" },
  { id: "practice_items", label: "Practice items", preset: false, value: (l) => (l.practice_items || []).join(" | ") },
  { id: "lesson_id", label: "Lesson ID", preset: false, value: (l) => l.lesson_id || "" },
];

export const presetExportFields = () => EXPORT_FIELDS.filter((f) => f.preset).map((f) => f.id);

/** One CSV cell, guarded against formula injection (exported for the org's
    session exports, which write through the same guard). */
export function csvEscape(value) {
  const s = value === null || value === undefined ? "" : String(value);
  // A leading =, +, - or @ makes Excel evaluate the cell as a formula. Lesson
  // titles are free text an instructor typed, so the guard belongs here rather
  // than in a trust assumption about what they typed.
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** Build the export as CSV text (Excel-compatible; see buildExportFilename). */
export function buildExportCsv(lessons, fieldIds, ctx = {}) {
  const fields = EXPORT_FIELDS.filter((f) => fieldIds.includes(f.id));
  if (!fields.length) return "";
  const header = fields.map((f) => csvEscape(f.label)).join(",");
  const rows = lessons.map((lesson) => fields.map((f) => csvEscape(f.value(lesson, ctx))).join(","));
  return [header, ...rows].join("\r\n");
}

export function buildExportFilename(period, now = new Date()) {
  const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  return `hans-lessons-${period}-${stamp}.csv`;
}
